import { useSelector } from 'react-redux';
import { RootState } from '../store/store';

export type FlyerType = 'pre' | 'post';

interface FlyerGenerationGate {
  isRunning: boolean;
  progress: number | null;
  url: string | null;
  canStart: boolean;
  isStale: boolean;
  startedAt: Date | null;
}

// A generation job that has been "in progress" for longer than this is
// considered stale (consumer likely crashed or pub/sub emulator is down in
// local dev). UI should surface an error and let the user retry.
const STALE_GENERATION_MS = 10 * 60 * 1000;

const toDate = (v: Date | string | null | undefined): Date | null => {
  if (!v) return null;
  const d = v instanceof Date ? v : new Date(v);
  return isNaN(d.getTime()) ? null : d;
};

export const useFlyerGenerationGate = (type: FlyerType): FlyerGenerationGate => {
  const user = useSelector((state: RootState) => state.user.user);

  const progress =
    type === 'pre' ? user?.preSessionFlyersGenerationProgress ?? null : user?.bookFlyersGenerationProgress ?? null;
  const url = type === 'pre' ? user?.preSessionFlyersPdfPath ?? null : user?.bookFlyersPdfPath ?? null;
  const startedAt = toDate(
    type === 'pre' ? user?.preSessionFlyersGenerationAt : user?.bookFlyersGenerationAt
  );

  const isRunning = progress !== null && progress !== undefined && progress >= 0 && progress < 100;
  const isStale = isRunning && !!startedAt && Date.now() - startedAt.getTime() > STALE_GENERATION_MS;

  return {
    isRunning: isRunning && !isStale,
    progress,
    url,
    canStart: !isRunning || isStale,
    isStale,
    startedAt,
  };
};
